import {
  createContext,
  Dispatch,
  ReactNode,
  Ref,
  RefObject,
  SetStateAction,
  useContext,
} from "react";

import { useModal } from "@newsela/angelou";

import {
  getCustomCollectionsWithHref,
  getPersonalizedCollectionsWithHref,
} from "@/base/NewselaGlobalLayoutV2/NavigationBar/NavigationBar.utils";
import useCollections from "@/collections/useCollections";
import { UseRestQueryResponse } from "@/remoteState/useRestQuery";
import useUser from "@/users/useUser";
import useUserLicenses from "@/users/useUserLicenses";
import { Collection } from "@/utils/collections/collections.constants";

const navigationBarV2ContextInitialValue: INavigationBarV2Context = {
  getters: {
    hasCollections: false,
    hasPDPassCollection: false,
    customCollections: [],
    personalizedCollections: [],
  },
  trialModal: {
    trialModalRef: { current: null },
    setIsTrialModalOpen: () => undefined,
    trialModalProps: {
      isActive: false,
      onEsc: () => undefined,
      closeModal: () => undefined,
      triggerRef: { current: null },
    },
  },
};

const NavigationBarV2Context = createContext(
  navigationBarV2ContextInitialValue,
);
const useNavigationBarV2Context = () => useContext(NavigationBarV2Context);

const NavigationBarProvider = ({ children }: INavigationBarV2Provider) => {
  const { data: collections = [] }: UseRestQueryResponse<INavmenuCollection[]> =
    useCollections();
  const userLicenses: IUserLicense[] = useUserLicenses();

  const [trialModalProps, setIsTrialModalOpen, trialModalRef] = useModal(false);

  const { loading: isUserLoading } = useUser();
  if (isUserLoading) return null;

  const customCollectionsWithHref = getCustomCollectionsWithHref(
    collections as INavmenuCollection[],
  );
  const personalizedCollectionsWithHref = getPersonalizedCollectionsWithHref(
    collections as INavmenuCollection[],
  );

  const hasCollections = !!userLicenses.find(
    (license) => license.product === "collections",
  );
  const hasPDPassCollection = !!(collections as INavmenuCollection[]).find(
    (collection) => collection.slug === "pd-pass-stream",
  );

  const getters = {
    hasCollections,
    hasPDPassCollection,
    customCollections: customCollectionsWithHref,
    personalizedCollections: personalizedCollectionsWithHref,
  };

  const trialModal = {
    trialModalRef,
    trialModalProps,
    setIsTrialModalOpen,
  };

  return (
    <NavigationBarV2Context.Provider value={{ getters, trialModal }}>
      {children}
    </NavigationBarV2Context.Provider>
  );
};

interface INavigationBarV2Context {
  getters: {
    hasCollections: boolean;
    hasPDPassCollection: boolean;
    customCollections: INavmenuCollection[];
    personalizedCollections: INavmenuCollection[];
  };
  trialModal: {
    trialModalRef: Ref<HTMLElement>;
    setIsTrialModalOpen: Dispatch<SetStateAction<boolean>>;
    trialModalProps: {
      isActive: boolean;
      onEsc: () => undefined;
      closeModal: () => undefined;
      triggerRef: RefObject<HTMLElement>;
    };
  };
}

interface INavigationBarV2Provider {
  children: ReactNode;
}

export interface IUserLicense {
  campaign: string;
  collection_ids: string[];
  date_ends: string;
  date_starts: string;
  id: number;
  is_active: boolean;
  is_expired: boolean;
  is_trial: boolean;
  product: string;
  type: string;
}

export interface INavmenuCollection extends Collection {
  href: string;
}

NavigationBarProvider.displayName = "NavigationBarProvider";
export { NavigationBarProvider, useNavigationBarV2Context };
